import AppIcon from '@/components/content/app/AppIcon'
import { getBrandColors, isPopulatedObject, cn } from '@/lib/utils'
import type { App, Media } from '@/payload-types'

type StackIconProps = React.HTMLAttributes<HTMLDivElement> & {
  apps?: App[]
}

export default function StackIconLarge({ apps }: StackIconProps) {
  const visibleApps = (apps ?? []).filter(Boolean) as App[]

  const SPEED = 25
  const APP_HEIGHT = 48
  const GAP = 18

  const minApps = 4
  const actualApps = visibleApps?.length ?? 0
  const targetApps = Math.max(minApps, Math.ceil(actualApps / 2) * 2)
  const emptyAppsNeeded = targetApps - actualApps
  const singleSetSize = (targetApps * APP_HEIGHT + (targetApps - 1) * GAP) / 2
  const duration = singleSetSize / SPEED

  const renderApps = (keySuffix = '') => {
    return (
      <>
        {visibleApps?.map((app, index) => {
          const appIcon = app?.icon as Media
          const appGlyph = app?.glyph as Media
          if (!isPopulatedObject<Media>(appIcon) && !isPopulatedObject<Media>(appGlyph)) {
            return null
          }
          const { backgroundColor } = getBrandColors(app)
          return (
            app && (
              <AppIcon
                key={`${app.id}-${index}${keySuffix}`}
                icon={appIcon}
                glyph={appGlyph}
                name={app.name}
                background={backgroundColor}
                size={48}
              />
            )
          )
        })}
        {emptyAppsNeeded > 0 &&
          Array.from({ length: emptyAppsNeeded }).map((_, index) => (
            <EmptyIcon key={`empty-${index}${keySuffix}`} />
          ))}
      </>
    )
  }

  return (
    <div className="relative z-20 h-[165px] w-[165px] shrink-0 overflow-hidden rounded-[42px] border-[3px] border-white/30 bg-black shadow-[0_10px_30px_0_rgba(0,0,0,1)] outline outline-[6px] -outline-offset-[9px] outline-white/15">
      <div className="relative top-[6px] h-[147px] overflow-hidden rounded-[42px]">
        <div
          className={cn(
            'flex flex-wrap items-center justify-center gap-4.5',
            visibleApps.length > 4
              ? 'pause group-hover:play animate-marquee-vertical transition-[animation-play-state] duration-300'
              : 'py-4.5',
          )}
          style={
            {
              '--duration': `${duration}s`,
              '--single-set-size': `${singleSetSize}px`,
              '--offset-start': `${GAP}px`,
              '--offset-end': `-${GAP / 2}px`,
            } as React.CSSProperties
          }
        >
          {renderApps('')}
          {visibleApps.length > 4 && renderApps('-dup')}
        </div>
      </div>
    </div>
  )
}

export function EmptyIcon() {
  return <div className="h-12 w-12 rounded-xl border-[0.75px] border-black/10 bg-black/5"></div>
}
